/**
 * ATL-1954 — the date label of a salary record on the Salary screen.
 *
 * A MONTHLY record is shown as its period (`startDate – endDate`); an hourly or daily one as its
 * `date`. The screen used to call `item.startDate.toDate()` unguarded. A monthly record read from
 * Supabase whose period never arrived (the dual-write did not send it) carries `startDate: null`,
 * the call threw during render and, with no error boundary, the whole app went white — the
 * merchant could neither see nor fix the record. UNDERWOOD, 2026-10-31.
 *
 * The cause is fixed where the period is written (the dual-write now carries it, and the stored
 * rows were backfilled). This module makes the screen state the truth about a record that still
 * lacks one — "period not set" — instead of crashing on it or inventing a period it does not have.
 *
 * The record's date fields arrive as a Firestore Timestamp (Firestore read path), a TimestampLike
 * with `toDate()` (API client read path), a `Date`, or an ISO string.
 */

type DateInput = { toDate: () => Date } | Date | string | number | null | undefined

export interface SalaryRecordDates {
  salaryType?: string
  date?: DateInput
  startDate?: DateInput
  endDate?: DateInput
}

/** The instant a record field holds, or `null` when it holds none (absent, null, unparseable). */
export const recordInstant = (value: DateInput): Date | null => {
  if (value === null || value === undefined) return null
  const d =
    typeof value === 'object' && !(value instanceof Date) && typeof value.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value as string | number)
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
}

export interface SalaryRecordDateLabelOptions {
  /** Renders one instant (the screen passes store-local conversion + `format(…, 'd/M/y EEEE')`). */
  formatDate: (instant: Date) => string
  /** Shown in place of a date the record does not carry. */
  missingLabel: string
}

/** The label the Salary screen shows for `item`. Never throws on a missing or malformed date. */
export const salaryRecordDateLabel = (
  item: SalaryRecordDates,
  { formatDate, missingLabel }: SalaryRecordDateLabelOptions,
): string => {
  const render = (value: DateInput): string => {
    const instant = recordInstant(value)
    return instant ? formatDate(instant) : missingLabel
  }
  if (item.salaryType === 'month') {
    const start = recordInstant(item.startDate)
    const end = recordInstant(item.endDate)
    if (!start && !end) return missingLabel
    return `${render(item.startDate)} - ${render(item.endDate)}`
  }
  return render(item.date)
}
